import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Funnel, FunnelXmark } from '@gravity-ui/icons';
import {
  TABLE_LIMIT_OPTIONS,
  clearTableFilters,
  clearTableLevel,
  countTableFilters,
  isTableFiltered,
  setTableLimit,
  setTableOffset,
  setTableSort,
  tableFilterLevels,
  toggleTableFilter,
} from '../../utils/analyticsTableParams';
import NumberedPagination from '../NumberedPagination/NumberedPagination';
import ColumnResizeHandle from '../ColumnResizeHandle/ColumnResizeHandle';
import { useResizableColumns } from '../../hooks/useResizableColumns';
import { TriStateCheckbox } from '../ScopeFilter/ScopeFilter';
import useTableFilterOptions from './useTableFilterOptions';
import '../ScopeFilter/ScopeFilter.css';
import './AnalyticsTable.css';

/*
 * ANALYTICS-TABLES01 (ТЗ v4 §24–26, FE-FIX21/22/23): один DataTable для всех
 * аналитических таблиц — те же checkbox-фильтры, та же серверная сортировка,
 * та же пагинация.
 *
 * Компонент ничего не считает и не сортирует: он показывает items ответа в
 * порядке backend, а total/offset/limit берёт из payload. Любое действие
 * пользователя — это новое состояние запроса, которое возвращается наружу
 * через onChange; страница пишет его в URL и перезапрашивает данные.
 *
 * Фильтр таблицы выглядит и работает как общий ScopeFilter страницы: кнопка
 * «Фильтр» с воронкой, раскрытая панель с колонками уровней, «Все …»,
 * черновик и «Применить». Сам ScopeFilter сюда не подходит — у таблиц свои
 * уровни (Объект / Локация / Камера + статус или смена).
 */

const LEVEL_HINT = {
  locationIds: 'Выберите объект, чтобы уточнить',
  cameraIds: 'Выберите локацию, чтобы уточнить',
};

const stateMessage = (levelKey, level) => {
  switch (level?.state) {
    case 'loading': return 'Загрузка…';
    case 'error': return 'Не удалось загрузить список';
    case 'empty': return 'Нет доступных значений';
    case 'idle': return LEVEL_HINT[levelKey] || '';
    default: return '';
  }
};

/** Направление сортировки — иконкой, не стрелкой-символом (docs/STYLE_GUIDE.md). */
const SortIcon = ({ order }) => {
  const Icon = order === 'asc' ? ChevronUp : ChevronDown;
  return <Icon width={16} height={16} aria-hidden />;
};

/**
 * Выбранный ID, которого нет в загруженных опциях (пришёл из ссылки), всё равно
 * рисуется чекбоксом: активный фильтр не должен быть невидимым.
 */
const buildRows = (options, selectedIds) => {
  const known = new Set(options.map((option) => option.id));
  return [
    ...options,
    ...selectedIds
      .filter((id) => !known.has(id))
      .map((id) => ({ id, name: `#${id}`, parentName: '', orphan: true })),
  ];
};

const filterValues = (table, state) =>
  Object.fromEntries(tableFilterLevels(table).map((level) => [level.key, state[level.key] || []]));

const sameFilters = (table, a, b) => tableFilterLevels(table).every((level) => {
  const left = a[level.key] || [];
  const right = b[level.key] || [];
  return left.length === right.length && left.every((id) => right.includes(id));
});

/** Уровень в том же виде, что у ScopeFilter: шапка с «все / выбрано N», «Все …», список. */
const FilterLevel = ({ level, selected, options, disabled, idPrefix, onToggle, onSelectAll, onClear }) => {
  const rows = level.options
    ? level.options.map((option) => ({ id: option.id, name: option.label, parentName: '' }))
    : buildRows(options?.options || [], selected);
  const message = level.options ? '' : stateMessage(level.key, options);
  const optionIds = rows.filter((row) => !row.orphan).map((row) => row.id);
  const allSelected = optionIds.length > 0 && optionIds.every((id) => selected.includes(id));
  const retry = options?.state === 'error' && (
    <button type="button" className="scope-level-retry" onClick={options.reload}>Повторить</button>
  );

  return (
    <fieldset className="scope-level">
      <legend className="scope-level-head">
        <span className="scope-level-label">{level.label}</span>
        <span className={selected.length ? 'scope-level-count scope-level-count--active' : 'scope-level-count'}>
          {selected.length === 0 ? 'все' : `выбрано ${selected.length}`}
        </span>
      </legend>

      {rows.length > 0 ? (
        <div className="scope-level-list">
          {optionIds.length > 0 && (
            <label className="scope-option scope-option--all">
              <TriStateCheckbox
                checked={allSelected}
                indeterminate={selected.length > 0 && !allSelected}
                disabled={disabled}
                id={`${idPrefix}-all-${level.key}`}
                onChange={() => (allSelected ? onClear() : onSelectAll(optionIds))}
              />
              <span className="scope-option-text">
                <span className="scope-option-name">{level.all}</span>
              </span>
            </label>
          )}
          {rows.map((option) => (
            <label key={option.id} className="scope-option">
              <input
                type="checkbox"
                checked={selected.includes(option.id)}
                disabled={disabled}
                onChange={() => onToggle(option.id)}
              />
              <span className="scope-option-text">
                <span className="scope-option-name">{option.name}</span>
                {option.parentName && <span className="scope-option-parent">{option.parentName}</span>}
              </span>
            </label>
          ))}
        </div>
      ) : (
        <div className="scope-level-placeholder">
          {message && <p className="scope-level-note">{message}</p>}
          {retry}
        </div>
      )}

      {rows.length > 0 && message && <p className="scope-level-note">{message}{retry}</p>}
      {options?.partial && (
        <p className="scope-level-note scope-level-note--warn">
          Часть списка не загрузилась — выбор может быть неполным
        </p>
      )}
    </fieldset>
  );
};

const AnalyticsTable = ({
  table,
  state,
  onChange,
  payload,
  columns,
  rowKey,
  title,
  note,
  scope,
  disabled = false,
  emptyText = 'Строк нет',
  idPrefix = 'atable',
  // ТЗ V5 №4: в «Сводке» ширина столбцов не настраивается — таблица на всю
  // ширину страницы и крупнее (size="large").
  resizable = true,
  size,
}) => {
  const [filtersOpen, setFiltersOpen] = useState(false);
  // Ширина столбцов тянется за край шапки; у каждой таблицы своя.
  const storedResize = useResizableColumns(`atable_${table.id}_widths`);
  const resize = resizable ? storedResize : { isResized: false };
  // Галочки меняют черновик, запрос уходит только по «Применить».
  // null — черновик совпадает с применённым выбором.
  const [draft, setDraft] = useState(null);
  const current = draft || state;
  const options = useTableFilterOptions({ state: current, scope, enabled: filtersOpen });
  const items = payload?.items || [];
  const levels = tableFilterLevels(table);
  const filterCount = countTableFilters(table, state);
  const draftCount = countTableFilters(table, current);
  const dirty = draft != null && !sameFilters(table, draft, state);
  // Сортировка отображается по ответу: пока новый запрос в пути, заголовок
  // показывает порядок, в котором строки реально пришли.
  const activeSortBy = payload?.sort_by || state.sortBy;
  const activeSortOrder = payload?.sort_order || state.sortOrder;

  // Из черновика берутся только фильтры: сортировка и размер страницы —
  // из актуального состояния, чтобы «Применить» их не откатывало.
  const apply = () => {
    onChange({ ...state, ...filterValues(table, current), offset: 0 });
    setDraft(null);
  };

  const collapse = () => {
    setFiltersOpen(false);
    setDraft(null);
  };

  const resetApplied = () => {
    setDraft(null);
    onChange(clearTableFilters(table, state));
  };

  // compact — столбец по ширине содержимого, свободное место уходит текстовым.
  const cellClass = (column) => [
    column.numeric && 'atable-num',
    column.compact && !resize.isResized && 'atable-compact',
  ].filter(Boolean).join(' ') || undefined;

  return (
    <section className={`atable${size === 'large' ? ' atable--large' : ''}`} aria-label={title}>
      <div className="atable-head">
        <h2 className="atable-title">{title}</h2>
        <div className="atable-head-actions">
          {!filtersOpen && (
            <button
              type="button"
              className="atable-btn atable-btn--icon"
              aria-expanded={false}
              onClick={() => setFiltersOpen(true)}
            >
              <Funnel width={16} height={16} aria-hidden />
              Фильтр
              {filterCount > 0 && <span className="atable-chip">Выбрано {filterCount}</span>}
            </button>
          )}
          {!filtersOpen && isTableFiltered(table, state) && (
            <button
              type="button"
              className="atable-btn atable-btn--icon"
              disabled={disabled}
              onClick={resetApplied}
              aria-label="Сбросить фильтр"
              title="Сбросить фильтр"
            >
              <FunnelXmark width={16} height={16} aria-hidden />
            </button>
          )}
          <label className="atable-limit">
            Строк
            <select
              value={state.limit}
              disabled={disabled}
              onChange={(event) => onChange(setTableLimit(table, state, Number(event.target.value)))}
            >
              {TABLE_LIMIT_OPTIONS.map((limit) => <option key={limit} value={limit}>{limit}</option>)}
            </select>
          </label>
        </div>
      </div>

      {note && <p className="atable-hint">{note}</p>}

      {filtersOpen && (
        <section className="scope-filter atable-filter" id={`${idPrefix}-filters`} aria-label="Фильтр таблицы">
          <div className="scope-filter-head">
            <span className="scope-filter-title">Фильтр таблицы</span>
            <span className="scope-filter-summary">
              {draftCount === 0 ? 'Все значения' : `Выбрано ${draftCount}`}
            </span>
            <button
              type="button"
              className="scope-filter-apply"
              disabled={disabled || !dirty}
              onClick={apply}
            >
              Применить
            </button>
            <button
              type="button"
              className="scope-filter-reset"
              disabled={disabled || draftCount === 0}
              onClick={() => setDraft(clearTableFilters(table, current))}
            >
              Сбросить всё
            </button>
            <button
              type="button"
              className="scope-filter-collapse"
              onClick={collapse}
              aria-label="Свернуть фильтр"
              title="Свернуть фильтр"
            >
              <ChevronUp width={16} height={16} aria-hidden />
            </button>
          </div>

          <div className="scope-filter-levels" style={{ '--scope-levels': levels.length }}>
            {levels.map((level) => (
              <FilterLevel
                key={level.key}
                level={level}
                selected={current[level.key] || []}
                options={options[level.key]}
                disabled={disabled}
                idPrefix={idPrefix}
                onToggle={(id) => setDraft(toggleTableFilter(table, current, level.key, id))}
                onSelectAll={(ids) => setDraft({ ...current, [level.key]: ids, offset: 0 })}
                onClear={() => setDraft(clearTableLevel(table, current, level.key))}
              />
            ))}
          </div>
        </section>
      )}

      <div className="atable-wrap">
        <table
          className={`atable-table${resize.isResized ? ' atable-table--resized' : ''}`}
          style={resize.isResized ? { width: resize.tableWidth(columns.map((column) => column.key)) } : undefined}
        >
          {resize.isResized && (
            <colgroup>
              {columns.map((column) => <col key={column.key} style={{ width: resize.columnWidth(column.key) }} />)}
            </colgroup>
          )}
          <thead>
            <tr>
              {columns.map((column) => {
                const sortable = column.sortKey && table.sortable.includes(column.sortKey);
                const active = sortable && activeSortBy === column.sortKey;
                return (
                  <th
                    key={column.key}
                    data-col-key={column.key}
                    className={cellClass(column)}
                    aria-sort={active ? (activeSortOrder === 'asc' ? 'ascending' : 'descending') : 'none'}
                  >
                    {sortable ? (
                      <button
                        type="button"
                        className={`atable-sort${active ? ' atable-sort--active' : ''}`}
                        disabled={disabled}
                        onClick={() => onChange(setTableSort(table, state, column.sortKey))}
                      >
                        {column.label}
                        {active && <SortIcon order={activeSortOrder} />}
                      </button>
                    ) : column.label}
                    {resizable && <ColumnResizeHandle columnKey={column.key} resize={resize} />}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={columns.length} className="atable-empty">{emptyText}</td></tr>
            )}
            {items.map((row, index) => (
              <tr key={rowKey ? rowKey(row) : (row.key ?? index)}>
                {columns.map((column) => (
                  <td key={column.key} className={cellClass(column)}>
                    {column.render(row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <NumberedPagination
        total={payload?.total ?? 0}
        limit={payload?.limit ?? state.limit}
        offset={payload?.offset ?? 0}
        disabled={disabled}
        onOffsetChange={(offset) => onChange(setTableOffset(table, state, offset))}
      />
    </section>
  );
};

export default AnalyticsTable;
